import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import zh from './locales/zh.json';
import rkEn from '@resource-kit/ui/locales/en.json';
import rkZh from '@resource-kit/ui/locales/zh.json';
import { createStore } from '@resource-kit/controllers';

// This array is the single source of truth for which catalogs ship:
// `scripts/check-i18n.py` reads it rather than hardcoding a list, so a
// locale added here is reconciled from that moment on. See
// `frontend/src/i18n/README.md`.
export const LOCALE_STORAGE_KEY = 'locale';
export const SUPPORTED_LOCALE_CODES = ['en', 'zh'] as const;
export type Locale = (typeof SUPPORTED_LOCALE_CODES)[number];
const localeStore = createStore<Locale>({
  key: LOCALE_STORAGE_KEY,
  version: 1,
  scope: 'device',
  sanitize: (raw) =>
    typeof raw === 'string' && (SUPPORTED_LOCALE_CODES as readonly string[]).includes(raw)
      ? (raw as Locale)
      : null,
});

function getInitialLocale(): string {
  const stored = localeStore.read();
  if (stored) return stored;
  const browser = navigator.language?.split('-')[0] ?? '';
  if ((SUPPORTED_LOCALE_CODES as readonly string[]).includes(browser)) return browser;
  return 'en';
}

i18n.use(initReactI18next).init({
  // MERGED into the one `translation` namespace, not registered as a second
  // one (#2169, ADR-0035 D7). The kit's keys all carry an `rk.` prefix and no
  // host key may (`i18n-key-parity.test.ts` enforces both directions), so the
  // flat key space stays unambiguous — and a kit component that translates a
  // key the HOST owns (a schema label, a resource title) still resolves it,
  // which a separate namespace plus `fallbackNS` would only do under
  // `nsSeparator` behaviour this app disables below.
  resources: {
    en: { translation: { ...en, ...rkEn } },
    zh: { translation: { ...zh, ...rkZh } },
  },
  lng: getInitialLocale(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  // Flat keys: the locale JSON files use dot-separated strings as literal
  // keys (e.g. `"audit.actor.cron:daily-dispatch"`), not nested objects.
  // i18next's default `keySeparator: '.'` and `nsSeparator: ':'` would
  // both chop such keys mid-string — in particular the cron-prefixed
  // `audit.actor.cron:daily-dispatch` was silently returning the raw key
  // instead of "Daily Dispatch" because the `:` was parsed as a
  // namespace boundary. Disabling both makes lookup a straight map hit.
  keySeparator: false,
  nsSeparator: false,
});

// Sync <html lang> and the locale preference on every language change.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  if ((SUPPORTED_LOCALE_CODES as readonly string[]).includes(lng)) {
    localeStore.write(lng as Locale);
  }
});

// Set initial <html lang>
document.documentElement.lang = i18n.language;

export default i18n;
